/**
 * Read Only Case Renderer
 *
 * A version of CaseRenderer that:
 * - Does NOT save progress to database
 * - Does NOT record answers
 * - Allows free navigation between all slides
 * - Shows correct/incorrect answers immediately
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { AnamnesePanel } from '@/components/masterclass/AnamnesePanel';
import { SlideLayout } from '@/components/masterclass/SlideLayout';
import { SupportingDocumentsPanel } from '@/components/masterclass/SupportingDocumentsPanel';
import { useTranslations } from '@/hooks/use-translations';
import { SlideFactory } from '@/modules/case/components/SlideFactory';
import { ValidationErrorDisplay } from '@/modules/case/components/ValidationErrorDisplay';
import { useCaseData } from '@/modules/case/hooks/useCaseData';
import { SlideType } from '@/modules/case/types';
import { doesMasterClassQuestionHaveACorrectAnswer } from '@/modules/case/utils/answer-validator';
import { getAssetById, getAssetUrl, getAssetsByRefs } from '@/modules/case/utils/asset-loader';
import { DisclaimerOverlay } from '@/modules/disclaimer';
import { isSlideQuestionSlide } from '@/utils/types/type-validation';
import { ValidationError } from '@/utils/types/validation';

import { ReadOnlyCaseRendererProps } from '../types';

export const ReadOnlyCaseRenderer = ({ caseId, onClose }: ReadOnlyCaseRendererProps) => {
  const { t } = useTranslations();
  const { caseConfig, assetsBaseUrl, loading, error } = useCaseData(caseId);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [videoEndAction, setVideoEndAction] = useState<'none' | 'next-slide' | 'close'>('none');
  const [isVideoComplete, setIsVideoComplete] = useState(true);
  const [isSpeakerVideoComplete, setIsSpeakerVideoComplete] = useState(true);
  const [isAnamnesePanelOpen, setIsAnamnesePanelOpen] = useState(false);
  const [isSupportingDocsPanelOpen, setIsSupportingDocsPanelOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  // Local answers state (not saved to DB)
  const [localAnswers, setLocalAnswers] = useState<Map<string, string[]>>(new Map());

  const hasInitialized = useRef(false);

  // All slides are visited in read-only mode
  const visitedSlideIds = useMemo(() => {
    return caseConfig?.slides.map((s) => s.id) || [];
  }, [caseConfig]);

  const handleDisclaimerComplete = () => {
    setShowDisclaimer(false);
  };

  const handleSlideChange = useCallback(
    (newSlideIndex: number) => {
      if (!caseConfig) return;

      setCurrentSlideIndex(newSlideIndex);

      const slide = caseConfig.slides[newSlideIndex];
      const slideMetadata = caseConfig.masterclass?.slideMap[slide.id];

      setIsSpeakerVideoComplete(true);
      setIsVideoComplete(true);

      if (slideMetadata?.introVideoRef) {
        const videoAsset = getAssetById(slideMetadata.introVideoRef, caseConfig.assets);
        if (videoAsset) {
          setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
          setVideoEndAction('close');
          setIsVideoComplete(true);
        }
      } else {
        setCurrentVideoUrl(null);
        setIsVideoComplete(true);
      }
    },
    [caseConfig, assetsBaseUrl]
  );

  useEffect(() => {
    if (caseConfig && currentSlideIndex === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      handleSlideChange(0);
    }
  }, [caseConfig, currentSlideIndex, handleSlideChange]);

  const handleNext = () => {
    if (!caseConfig) return;

    const currentSlide = caseConfig.slides[currentSlideIndex];
    const slideMetadata = caseConfig.masterclass?.slideMap[currentSlide.id];

    // Last slide - close
    if (currentSlideIndex === caseConfig.slides.length - 1) {
      onClose();
      return;
    }

    // Check for outro video
    if (slideMetadata?.outroVideoRef) {
      const videoAsset = getAssetById(slideMetadata.outroVideoRef, caseConfig.assets);
      if (videoAsset) {
        setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
        setVideoEndAction('next-slide');
        return;
      }
    }

    // Navigate to next slide
    if (currentSlideIndex < caseConfig.slides.length - 1) {
      handleSlideChange(currentSlideIndex + 1);
    }
  };

  // Handle answer selection - NO DATABASE SAVE
  // eslint-disable-next-line @typescript-eslint/require-await
  const handleAnswerSelected = async (answerId: string, isCorrect: boolean, videoRef?: string) => {
    if (!caseConfig) return;

    const currentSlide = caseConfig.slides[currentSlideIndex];
    const slideId = currentSlide.id;

    // Update local answers state only
    setLocalAnswers((prev) => {
      const newMap = new Map(prev);
      const currentAnswers = newMap.get(slideId) || [];
      if (!currentAnswers.includes(answerId)) {
        newMap.set(slideId, [...currentAnswers, answerId]);
      }
      return newMap;
    });

    // Play video feedback if exists
    if (videoRef) {
      const videoAsset = getAssetById(videoRef, caseConfig.assets);
      if (videoAsset) {
        setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
        setVideoEndAction('close');
        setIsVideoComplete(false);
      }
    }
  };

  const handleVideoEnd = () => {
    setIsVideoComplete(true);

    if (videoEndAction === 'next-slide') {
      if (currentSlideIndex < (caseConfig?.slides.length || 0) - 1) {
        handleSlideChange(currentSlideIndex + 1);
      } else {
        onClose();
      }
    }

    setCurrentVideoUrl(null);
    setVideoEndAction('none');
  };

  const handleSpeakerVideoComplete = useCallback(() => {
    setIsSpeakerVideoComplete(true);
  }, []);

  const handleVideoReplay = useCallback((url: string) => {
    setCurrentVideoUrl(url);
    setVideoEndAction('close');
    setIsVideoComplete(false);
  }, []);

  // Build answered questions map from local answers
  const slideIdToAnswerids: Map<string, string[]> = localAnswers;

  // Convert local answers to the format expected by SlideFactory
  // Using mock values for fields not needed in read-only mode
  const answersForFactory = useMemo(() => {
    const result: Array<{
      id: string;
      case_progress_id: string;
      slide_id: string;
      user_id: string;
      answer_id: string;
      created_at: string;
      case_id: string;
    }> = [];
    localAnswers.forEach((answerIds, slideId) => {
      answerIds.forEach((answerId, index) => {
        result.push({
          id: `read-only-${slideId}-${index}`,
          case_progress_id: 'read-only',
          slide_id: slideId,
          user_id: 'read-only',
          answer_id: answerId,
          created_at: new Date().toISOString(),
          case_id: caseId,
        });
      });
    });
    return result;
  }, [localAnswers, caseId]);

  if (error) {
    if (error instanceof ValidationError) {
      return <ValidationErrorDisplay error={error} caseKey={caseId} />;
    }

    const errorMessage = typeof error === 'string' ? error : error.message;
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">
            {t('common.messages.error', { message: errorMessage })}
          </p>
        </div>
      </div>
    );
  }

  if (loading || !caseConfig || !caseConfig.masterclass) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('common.messages.loadingCase')}</p>
        </div>
      </div>
    );
  }

  const currentSlide = caseConfig.slides[currentSlideIndex];
  const doesSlideHaveACorrectAnswer =
    isSlideQuestionSlide(currentSlide) &&
    doesMasterClassQuestionHaveACorrectAnswer(caseConfig, currentSlide.id);

  // In read-only mode: no answer requirements block navigation
  const requiresAnswer = false;
  const hasAnsweredCorrectly = true;

  const anamneseSlide = caseConfig.slides.find((s) => s.type === 'anamnese');
  const anamneseData = anamneseSlide?.items.find((item) => item.type === 'anamnese') || null;
  const supportingDocuments = getAssetsByRefs(currentSlide.libraryRefs, caseConfig.assets);

  if (showDisclaimer && caseConfig?.disclaimer) {
    return (
      <DisclaimerOverlay
        disclaimer={caseConfig.disclaimer}
        assetsBaseUrl={assetsBaseUrl}
        onComplete={handleDisclaimerComplete}
      />
    );
  }

  return (
    <>
      {/* Anamnese Panel */}
      <AnamnesePanel
        isOpen={isAnamnesePanelOpen}
        onClose={() => setIsAnamnesePanelOpen(false)}
        anamneseData={anamneseData}
        allAssets={caseConfig.assets}
        assetsBaseUrl={assetsBaseUrl}
      />

      {/* Supporting Documents Panel */}
      <SupportingDocumentsPanel
        isOpen={isSupportingDocsPanelOpen}
        onClose={() => setIsSupportingDocsPanelOpen(false)}
        assets={supportingDocuments}
        assetsBaseUrl={assetsBaseUrl}
      />

      <div className="h-full">
        <SlideLayout
          currentSlide={currentSlideIndex}
          totalSlides={caseConfig.slides.length}
          onSlideChange={handleSlideChange}
          onNext={handleNext}
          title={currentSlide.title}
          subtitle={caseConfig.displayName}
          caseNumber={caseId || '1'}
          onHomeClick={onClose}
          visitedSlideIds={visitedSlideIds}
          currentSlideId={currentSlide.id}
          slides={caseConfig.slides}
          videoUrl={currentVideoUrl}
          onVideoEnd={handleVideoEnd}
          onSpeakerVideoComplete={handleSpeakerVideoComplete}
          onVideoReplay={handleVideoReplay}
          isVideoComplete={isVideoComplete}
          isSpeakerVideoComplete={isSpeakerVideoComplete}
          requiresAnswer={requiresAnswer}
          hasAnsweredCorrectly={hasAnsweredCorrectly}
          onAnamneseClick={() => setIsAnamnesePanelOpen(true)}
          onDocsClick={() => setIsSupportingDocsPanelOpen(true)}
          docsCount={supportingDocuments.length}
          hideSpeakerVideo={currentSlide.type === SlideType.VIDEO}
          showReviewerBadge={true}
        >
          <SlideFactory
            slide={currentSlide}
            assets={caseConfig.assets}
            assetsBaseUrl={assetsBaseUrl}
            masterClass={caseConfig.masterclass}
            onNext={handleNext}
            onPrevious={() => currentSlideIndex > 0 && setCurrentSlideIndex(currentSlideIndex - 1)}
            onAnswerSelected={handleAnswerSelected}
            answeredQuestions={slideIdToAnswerids}
            allAnswers={answersForFactory}
            caseConfig={caseConfig}
          />
        </SlideLayout>
      </div>
    </>
  );
};
